import React, { useCallback, useContext, useEffect, useState } from 'react'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { AppCtx } from '@paytrace/pt-skel/Context'
import Event from '../Event'
import {
  ACHMerchantSettings,
  Customer,
  CustomerAchDetails,
  Recur,
  RECUR_FREQUENCY_VALUE,
  RECUR_PAYMENT_TYPE,
  RECUR_PAYMENT_VALUE,
  RECUR_TRANSACTION_TYPE,
  RecurAttemptMap,
  RecurFormData,
  RecurFrequencyMap,
  RecurPaymentTypes,
  RecurRequest,
  RecurTransactionTypeMap,
  stdEntryClasses,
} from '../types'
import { formatDateToMMDDYYYY, formatDateToYYYYMMDD } from '../Helper/util'
import useGetTransactionPreview from './useGetTransactionPreview'

interface SelectOption {
  val: string | number;
  name: string;
}

interface UseRecurFormLogicProps {
  isEditMode: boolean;
  initialRecurData?: Recur | null;
  customer?: Customer | null;
  companyState?: string;
}

interface UseRecurFormLogicReturn {
  formData: RecurFormData;
  setFormData: React.Dispatch<React.SetStateAction<RecurFormData>>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | { target: { name: string; value: any } }) => void;
  handleSubmit: (event: React.FormEvent) => void;
  handleDelete: (event: React.FormEvent) => void;
  validationErrors: Record<string, string>;
  showValidationErrors: () => React.ReactElement | null;
  supportedRecurTranxTypes: SelectOption[];
  supportedStdEntryClasses: SelectOption[];
  achMerchantSettings: ACHMerchantSettings | undefined;
  minDate: Date;
  recurResponse: { status: string; message: string };
  isLoading: boolean;
  disableSubmit: boolean;
  showSurchargeInfo: boolean;
  isStateSurchargeAvailable: boolean;
  isStateAvailable: boolean;
  isStateCheckAlertOpen: boolean;
  setIsStateCheckAlertOpen: React.Dispatch<React.SetStateAction<boolean>>;
  showDeleteButton: boolean;
  customerAchDetails: CustomerAchDetails;
}

const defaultFormData: RecurFormData = {
  transaction_type: RECUR_TRANSACTION_TYPE.SALE,
  amount: 0.0,
  surcharge_amount: 0.0,
  total_amount: 0.0,
  frequency: RECUR_FREQUENCY_VALUE.DAILY ?? '',
  process_on_weekends: false,
  start_date: undefined,
  next_date: undefined,
  transaction_count: undefined,
  no_of_payment_processed: '0',
  no_of_attempts_if_declined: Number(RecurAttemptMap[0]?.val ?? 0),
  payment_type: RECUR_PAYMENT_VALUE.CREDIT_CARD ?? '',
  description: '',
  customer_receipt: true,
  sec_code: '',
  id: 0,
  customer_billing_name: '',
  customer_custid: '',
  when: '',
}

const mapRecurToFormData = (recurData: Recur): RecurFormData => {
  return {
    ...defaultFormData,
    id: recurData.recurring_id,
    transaction_type: recurData.transaction_type,
    amount: recurData.amount,
    frequency: recurData.frequency,
    process_on_weekends: recurData.process_on_weekend === 'Y',
    start_date: recurData.start_date,
    next_date: recurData.next_processing_date,
    no_of_payment_processed: recurData.current_count,
    no_of_attempts_if_declined: recurData.repeat,
    payment_type: recurData.recurring_type,
    description: recurData.description,
    customer_receipt: recurData.customer_receipt === 'Y',
    sec_code: recurData.sec_code,
    customer_billing_name: recurData.customer_billing_name,
    customer_custid: recurData.customer_custid,
    username: recurData.username,
    ip_address: recurData.ip_address,
    when: recurData.when,
    transaction_count: recurData.total_count,
    merchant_local_time: recurData.merchant_local_time,
    add_surcharge: recurData.add_surcharge,
  }
}

const self = Symbol('useRecurFormLogic')

export function useRecurFormLogic({
  isEditMode,
  initialRecurData,
  customer,
  companyState,
}: UseRecurFormLogicProps): UseRecurFormLogicReturn {
  const context = useContext(AppCtx)
  const merchantId = context.merchant

  const [formData, setFormData] = useState<RecurFormData>(() => {
    if (isEditMode && initialRecurData) {
      return mapRecurToFormData(initialRecurData)
    }
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return { ...defaultFormData, start_date: tomorrow }
  })

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [supportedRecurTranxTypes, setSupportedRecurTranxTypes] = useState<SelectOption[]>(RecurTransactionTypeMap)
  const [supportedStdEntryClasses, setSupportedStdEntryClasses] = useState<SelectOption[]>(stdEntryClasses)
  const [recurResponse, setRecurResponse] = useState({ status: '', message: '' })
  const [achMerchantSettings, setAchMerchantSettings] = useState<ACHMerchantSettings | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false)
  const [showSurchargeInfo, setShowSurchargeInfo] = useState<boolean>(false)
  const [isStateSurchargeAvailable, setStateIsSurchargeAvailable] = useState<boolean>(false)
  const [isStateAvailable, setIsStateAvailable] = useState<boolean>(false)
  const [isStateCheckAlertOpen, setIsStateCheckAlertOpen] = useState<boolean>(false)
  const surchargeInfo = useGetTransactionPreview('', merchantId, customer?.customer_id, formData?.amount)
  const [showDeleteButton, setShowDeleteButton] = useState<boolean>(false)
  const [customerAchDetails, setCustomerAchDetails] = useState<CustomerAchDetails | null>(null)

  const calculateMinDate = useCallback(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow
  }, [])

  const [minDate] = useState<Date>(() => calculateMinDate())

  useEffect(() => {
    if (isEditMode && initialRecurData) {
      setFormData(mapRecurToFormData(initialRecurData))
      setValidationErrors({})
      setRecurResponse({ status: '', message: '' })
    } else if (!isEditMode) {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      setFormData({ ...defaultFormData, start_date: tomorrow })
      setValidationErrors({})
      setRecurResponse({ status: '', message: '' })
    }
  }, [initialRecurData, isEditMode])

  useEffect(() => {
    const merchantSurchargeIneligibilityReason =
      surchargeInfo?.surcharge_preview_response?.surcharge_ineligibility_reason || ''

    const isBinSurchargeable =
      surchargeInfo?.bin_preview_response?.is_surchargeable

    if (surchargeInfo && !merchantSurchargeIneligibilityReason && isBinSurchargeable && formData.payment_type !== RECUR_PAYMENT_VALUE.BANK_ACCOUNT) {
      setShowSurchargeInfo(true)
    } else {
      setShowSurchargeInfo(false)
      setFormData((prev) => ({
        ...prev,
        add_surcharge: false,
      }))
    }
  }, [surchargeInfo, formData.payment_type])

  useEffect(() => {
    if (!context.records.user.has_read_only_merchant_access_reseller || context.records.user.is_admin
    ) {
      setShowDeleteButton(true)
    }
  })

  useEffect(() => {
    if (merchantId !== undefined) {
      PubSub.pub(Event.GET_ACH_MERCHANT_SETTINGS_REQUEST, { merchantId })
    }
  }, [merchantId])

  useEffect(() => {
    const handleSettingsResponse = (settings: ACHMerchantSettings) => {
      setAchMerchantSettings(settings)
      if (settings?.std_entry_classes) {
        const filteredStdClasses = stdEntryClasses.filter(
          (item) => settings.std_entry_classes.includes(String(item.val)) || item.val === ''
        )
        setSupportedStdEntryClasses(filteredStdClasses)
      } else {
        setSupportedStdEntryClasses(stdEntryClasses.filter(item => item.val === '')) // Only allow empty if no settings
      }
    }

    PubSub.sub(self, Event.GET_ACH_MERCHANT_SETTINGS_RESPONSE, handleSettingsResponse)

    return () => {
      PubSub.unsub(self, Event.GET_ACH_MERCHANT_SETTINGS_RESPONSE, handleSettingsResponse)
    }
  }, [])

  useEffect(() => {
    const mapCustomerAchDetails = (data: any) => {
      setCustomerAchDetails(data.data)
      const achCustomerSecCode = data.data?.std_entry_class
      if (achCustomerSecCode !== undefined || achCustomerSecCode !== '') {
        setFormData(prevData => ({
          ...prevData,
          sec_code: achCustomerSecCode,
        }))
      }
    }

    PubSub.pub(Event.GET_CUSTOMER_ACH_DETAILS_REQUEST, { customer_id: customer?.customer_id })
    PubSub.sub(self, Event.GET_CUSTOMER_ACH_DETAILS_RESPONSE, mapCustomerAchDetails)

    return () => {
      PubSub.unsub(Event.GET_CUSTOMER_ACH_DETAILS_RESPONSE)
    }
  }, [customer, customer?.customer_id, setCustomerAchDetails])

  useEffect(() => {
    const handleResponse = (response: any) => {
      setIsLoading(false)
      if (response.status_code >= 400) {
        const errorMessage = response.data?.[0]?.detail || response.message || 'An unknown error occurred.'
        setRecurResponse({ status: 'error', message: errorMessage })
      } else {
        setRecurResponse({
          status: 'success',
          message: response?.data?.status_message,
        })
      }
    }

    const postEvent = Event.RUN_RECURRING_PAYMENT_POST_RESPONSE
    const putEvent = Event.RUN_RECURRING_PAYMENT_PUT_RESPONSE
    const deleteEvent = Event.DELETE_RECURRING_PAYMENT_RESPONSE

    PubSub.sub(self, postEvent, handleResponse)
    PubSub.sub(self, putEvent, handleResponse)
    PubSub.sub(self, deleteEvent, handleResponse)
  })

  useEffect(() => {
    if (achMerchantSettings?.processor_code === 'VCI' && formData.payment_type === RECUR_PAYMENT_VALUE.BANK_ACCOUNT) {
      if (formData.process_on_weekends === true) {
        setFormData(prevData => ({
          ...prevData,
          process_on_weekends: false,
        }))
      }
    }
  }, [formData.process_on_weekends, formData.payment_type, achMerchantSettings])

  useEffect(() => {
    const shouldDisable = (() => {
      if ((formData?.amount || 0) <= 0) {
        return true
      }
      if (formData.transaction_count === undefined || formData.transaction_count === '') {
        return true
      }
      if (formData.payment_type === RECUR_PAYMENT_VALUE.CREDIT_CARD &&
        formData.add_surcharge &&
        !formData.confirm_surcharge_enable) {
        return true
      }
      if (formData.payment_type === RECUR_PAYMENT_VALUE.BANK_ACCOUNT &&
        !formData.sec_code) {
        return true
      }
      return false
    })()
    setDisableSubmit(shouldDisable)
  }, [formData])

  useEffect(() => {
    if (surchargeInfo) {
      const surchargeAmount = parseFloat((surchargeInfo?.surcharge_preview_response?.surcharge_amount || 0.0).toFixed(2))
      const totalAmount = parseFloat((Number(formData?.amount) + surchargeAmount).toFixed(2))
      setFormData(prevData => ({
        ...prevData,
        surcharge_amount: surchargeAmount,
        total_amount: totalAmount,
      }))
    }
  }, [formData.amount, surchargeInfo, customer?.customer_id])

  const handleChange = useCallback(
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
        | { target: { name: string; value: any } }
    ) => {
      // Get name, value, type, checked safely
      const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement & { name: string; value: any; type?: string; checked?: boolean }
      const { name, value, type } = target

      // For checkboxes, handle add_surcharge logic
      if (type === 'checkbox' && 'checked' in target) {
        if (name === 'add_surcharge' && target.checked) {
          // Check state and surchargeInfo
          const hasState = customer?.state !== ''
          const isSurchargeable = !surchargeInfo?.bin_preview_response?.is_surchargeable

          setIsStateAvailable(hasState)
          setStateIsSurchargeAvailable(isSurchargeable)
          if (hasState && isSurchargeable) {
            setIsStateCheckAlertOpen(true)
            // Uncheck add_surcharge
            setFormData(prev => ({
              ...prev,
              add_surcharge: false,
            }))
            return
          }
          // Always enable customer receipt when add_surcharge is enabled
          setFormData(prev => ({
            ...prev,
            customer_receipt: true,
          }))
        }

        setFormData(prev => ({
          ...prev,
          [name]: target.checked,
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value,
        }))
      }

      if (validationErrors[name]) {
        setValidationErrors(prev => ({
          ...prev,
          [name]: '',
        }))
      }
      if (recurResponse.message) {
        setRecurResponse({ status: '', message: '' })
      }
    },
    [customer, surchargeInfo, validationErrors, recurResponse.message]
  )

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {}

    if (!customer?.customer_id) {
      errors.customer = 'Please select a valid <b>Customer Profile</b>.'
    }

    if (formData.amount == null || isNaN(Number(formData.amount))) {
      errors.amount = 'Please provide a valid <b>Amount</b>.'
    } else if (Number(formData.amount) < 0) {
      errors.amount = 'Amount must be a positive number.'
    } else if (!/^\d+(\.\d{0,2})?$/.test(String(formData.amount))) {
      errors.amount = 'Amount must have at most two decimal places.'
    } else if (formData.transaction_type === RECUR_TRANSACTION_TYPE.SALE && Number(formData.amount) > Number(context.records.user.sale_ceiling)) {
      errors.amount = `Maximum allowed amount is ${Number(context.records.user.sale_ceiling)}`
    }

    const allowedFrequencies = RecurFrequencyMap.map(f => f.val)
    if (!formData.frequency || !allowedFrequencies.includes(formData.frequency)) {
      errors.frequency = 'Please provide a valid <b>Frequency</b>.'
    }

    const dateToValidate = isEditMode ? formData.next_date : formData.start_date
    const dateFieldName = isEditMode ? 'Next Date' : 'Start Date'
    const dateKey = isEditMode ? 'next_date' : 'start_date'

    if (!dateToValidate || dateToValidate === undefined) {
      errors[dateKey] = `Please provide a valid <b>${dateFieldName}</b>.`
    }

    const count = formData.transaction_count
    if (count == null || count === '') {
      errors.transaction_count = 'Please provide a valid <b>Transaction Count</b>.'
    } else {
      const countStr = String(count)
      if (!/^\d+$/.test(countStr) || parseInt(countStr, 10) <= 0) {
        if (countStr !== '999') {
          errors.transaction_count = 'Transaction count must be a positive number or 999.'
        }
      } else if (countStr.length > 3) {
        errors.transaction_count = 'Transaction count cannot exceed 3 digits (use 999 for indefinite).'
      }
    }

    if (formData.payment_type === RECUR_PAYMENT_VALUE.BANK_ACCOUNT && (!formData.transaction_type || formData.transaction_type === RECUR_TRANSACTION_TYPE.AUTHORIZATION)) {
      errors.transaction_type = 'Please provide a valid <b>Transaction Type</b>.'
    }

    if (formData.payment_type === RECUR_PAYMENT_VALUE.BANK_ACCOUNT && achMerchantSettings?.processor_code === 'VCI' && !formData.sec_code) {
      errors.sec_code = 'Please provide a valid <b>SEC Code</b>.'
    } else if (formData.sec_code) {
      const allowedSECCodes = stdEntryClasses.map(s => s.val).filter(Boolean)
      if (!allowedSECCodes.includes(formData.sec_code)) {
        errors.sec_code = 'Invalid SEC Code selected.'
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [formData, customer, isEditMode, achMerchantSettings, supportedRecurTranxTypes, calculateMinDate])

  const handleSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault()
    setRecurResponse({ status: '', message: '' })

    if (validate()) {
      setIsLoading(true)
      const commonRecurrenceData = {
        amount: Number(formData.amount),
        customer_receipt: formData.customer_receipt,
        frequency: String(formData.frequency),
        transaction_type: String(formData.transaction_type),
        type: String(formData.payment_type),
        description: formData.description || '',
        weekend: formData.process_on_weekends,
        repeat: Number(formData.no_of_attempts_if_declined),
        total_count: String(formData.transaction_count),
      }

      let request: RecurRequest
      let pubEvent: string

      if (isEditMode) {
        request = {
          merchant_id: Number(merchantId),
          customer_id: Number(customer!.customer_id),
          recurrence: {
            ...commonRecurrenceData,
            id: formData.id,
          },
          next_date: formData.next_date ? formatDateToMMDDYYYY(formData.next_date) : undefined,
        }
        pubEvent = Event.RUN_RECURRING_PAYMENT_PUT_REQUEST
      } else {
        request = {
          merchant_id: Number(merchantId),
          customer_id: Number(customer!.customer_id),
          recurrence: {
            ...commonRecurrenceData,
            start_date: formData.start_date ? formatDateToMMDDYYYY(formData.start_date) : undefined,
          },
        }
        pubEvent = Event.RUN_RECURRING_PAYMENT_POST_REQUEST
      }
      if (formData.payment_type === RECUR_PAYMENT_VALUE.BANK_ACCOUNT) {
        request.recurrence.sec_code = formData.sec_code
      }

      if (formData.payment_type === RECUR_PAYMENT_VALUE.CREDIT_CARD) {
        request.surcharge_info ={
          omit_surcharge: true, // creating the surcharge info object first
        }
        request.surcharge_info.omit_surcharge = !formData.add_surcharge
      }

      PubSub.pub(pubEvent, request)
    } else {
      // setRecurResponse({ status: 'error', message: 'Please fix the errors below.' });
    }
  }, [validate, formData, isEditMode, merchantId, customer, validationErrors])

  const handleDelete = useCallback((event: React.FormEvent) => {
    event.preventDefault()
    const msg = 'Are you sure you wish to delete this recurring payment?'
    const confirmed = confirm(
      msg
    )
    if (!confirmed) {
      return
    }
    setRecurResponse({ status: '', message: '' })
    setIsLoading(true)
    const request = {
      merchant_id: merchantId,
      recurrence: {
        id: formData.id,
      },
    }
    PubSub.pub(Event.DELETE_RECURRING_PAYMENT_REQUEST, request)
  }, [formData, isEditMode, merchantId])

  const showValidationErrors = useCallback((): React.ReactElement | null => {
    const errorMessages = Object.values(validationErrors).filter(Boolean)
    if (errorMessages.length === 0) {
      return null
    }
    return (
      <div className="error error-feedback">
        {errorMessages.map((errorMessage, index) => (
          <div key={index} dangerouslySetInnerHTML={{ __html: errorMessage }} />
        ))}
      </div>
    )
  }, [validationErrors])

  return {
    formData,
    setFormData,
    handleChange,
    handleSubmit,
    handleDelete,
    validationErrors,
    showValidationErrors,
    supportedRecurTranxTypes,
    supportedStdEntryClasses,
    achMerchantSettings,
    minDate,
    recurResponse,
    isLoading,
    disableSubmit,
    showSurchargeInfo,
    isStateAvailable, // is the "State" ("WA", "CA", "TX") available from the customer
    isStateSurchargeAvailable, // is SurchargeAvailable for the custoemr
    setIsStateCheckAlertOpen,
    isStateCheckAlertOpen,
    showDeleteButton,
    customerAchDetails,
  }
}
